import {
  alpha3ToAlpha2,
  attachmentSizeReading,
  type CountryOption,
  countryName,
  countryOptionFor,
  countryOptions,
  formatMinor,
  HOST_FORMAT_LOCALE,
  type HostLocale,
  localDateInTimezone,
  MS_PER_DAY,
  parseIsoDate,
  parseLocalTime,
  rateAmountReading,
  STAY_DAY_ZONE,
} from "@vidimo/shared";
import {
  createFormatter,
  type DateTimeFormatOptions,
  useLocale,
} from "use-intl";
import { createIntlPolicy, intlPolicyMode } from "./intl-policy";
import { getInterfaceLocaleState } from "./locale-store";

/** The fields each kind of date label shows; examples are English. */
const DATE_SHAPES = {
  /** "Fri 5 Dec, 17:00" */
  dateTime: {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    weekday: "short",
  },
  /** "Fri 5 Dec" */
  day: { day: "numeric", month: "short", weekday: "short" },
  /** "5 December" */
  dayMonth: { day: "numeric", month: "long" },
  /** "5 December, 17:00" */
  dayMonthTime: {
    day: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
    minute: "2-digit",
    month: "long",
  },
  /** "5" */
  dayOfMonth: { day: "numeric" },
  /** "Friday, 5 December 2025" */
  longDate: { day: "numeric", month: "long", weekday: "long", year: "numeric" },
  /** "Friday, 5 December" */
  longDay: { day: "numeric", month: "long", weekday: "long" },
  /** "5 Dec 2025" */
  mediumDate: { day: "numeric", month: "short", year: "numeric" },
  /** "Dec" */
  month: { month: "short" },
  /** "December 2025" */
  monthYear: { month: "long", year: "numeric" },
  /** "5 Dec" */
  shortDate: { day: "numeric", month: "short" },
  /** "5 Dec, 17:00" */
  shortDateTime: {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  },
  /** "17:00" */
  time: { timeStyle: "short" },
  /** "Friday" */
  weekday: { weekday: "long" },
  /** "Fri 5" */
  weekdayDay: { day: "numeric", weekday: "short" },
  /** "Fri 5 December" */
  weekdayDayMonth: { day: "numeric", month: "long", weekday: "short" },
  /** "F" */
  weekdayNarrow: { weekday: "narrow" },
} satisfies Record<string, DateTimeFormatOptions>;

export type DateShape = keyof typeof DATE_SHAPES;

type DateInput = Date | string | null | undefined;

/**
 * The zone of an instant the host lives through where they stand (a message,
 * a task's start), as opposed to a property's zone or a stay day's.
 */
export const DEVICE_ZONE: null = null;

const DAYS_IN_WEEK = 7;

/** The 'YYYY' that opens a 'YYYY-MM-DD'. */
const YEAR_LENGTH = 4;

/** A Sunday, so day `n` of the week is `n` days after it. */
const A_SUNDAY = Date.UTC(2023, 0, 1);

const { onError } = createIntlPolicy(intlPolicyMode());

/** Coerce the loose inputs callers pass, or null when there is nothing. */
function toDate(value: DateInput): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Zones a host typed are free text: one the runtime rejects reads as UTC. */
const checkedZones = new Map<string, string>();

function validZone(zone: string): string {
  let checked = checkedZones.get(zone);
  if (!checked) {
    try {
      checked = new Intl.DateTimeFormat("en", {
        timeZone: zone,
      }).resolvedOptions().timeZone;
    } catch {
      checked = STAY_DAY_ZONE;
    }
    checkedZones.set(zone, checked);
  }
  return checked;
}

function readDeviceZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Resolving the device's zone costs more than the format call it serves, so
 * it is cached. The host can travel: it is read again whenever the app comes
 * back to the foreground, which the native webview reports as `visibilitychange`.
 */
let deviceZone = readDeviceZone();

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      deviceZone = readDeviceZone();
    }
  });
}

function zoneOrDevice(timeZone: string | null | undefined): string {
  return timeZone ? validZone(timeZone) : deviceZone;
}

/** Calendar days from `nowDay` to `day`, both 'YYYY-MM-DD'. */
function daysBetween(nowDay: string, day: string): number {
  return Math.round(
    (parseIsoDate(day).getTime() - parseIsoDate(nowDay).getTime()) / MS_PER_DAY
  );
}

function formatFor(locale: HostLocale) {
  const tag = HOST_FORMAT_LOCALE[locale];
  const format = createFormatter({
    // A formatting tag, not an interface locale, which is all use-intl types.
    locale: tag as HostLocale,
    onError,
  });
  const relative = new Intl.RelativeTimeFormat(tag, {
    numeric: "auto",
  });
  const rangeFormatters = new Map<string, Intl.DateTimeFormat>();

  /**
   * `value` in `shape`, on the wall clock of `timeZone` (the device's when
   * none). A stay day passes `STAY_DAY_ZONE`. "" for a missing or unparseable
   * value: a label degrades to blank rather than "Invalid Date".
   */
  function date(
    value: DateInput,
    shape: DateShape,
    timeZone: string | null | undefined
  ): string {
    const at = toDate(value);
    return at
      ? format.dateTime(at, {
          ...DATE_SHAPES[shape],
          timeZone: zoneOrDevice(timeZone),
        })
      : "";
  }

  /** "Today", "Yesterday" or "Tomorrow", capitalised; null past a day away. */
  function relativeDay(days: number): string | null {
    if (Math.abs(days) > 1) {
      return null;
    }
    const words = relative.format(days, "day");
    return words.charAt(0).toLocaleUpperCase(locale) + words.slice(1);
  }

  /**
   * Two dates in `shape` as one range, what they share said once:
   * "5–7 Dec 2025"; one date when they fall on the same day.
   */
  function dateRange(
    from: Date | string,
    to: Date | string,
    shape: DateShape,
    timeZone: string | null | undefined
  ): string {
    const zone = zoneOrDevice(timeZone);
    const key = `${shape}|${zone}`;
    let formatter = rangeFormatters.get(key);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat(tag, {
        ...DATE_SHAPES[shape],
        timeZone: zone,
      });
      rangeFormatters.set(key, formatter);
    }
    const options: DateTimeFormatOptions = DATE_SHAPES[shape];
    const unpadDay = options.day === "numeric";
    // CLDR's Italian range patterns pad the day ("05–06 ott") where its single
    // dates do not, so a numeric day is unpadded here for every language.
    const start = toDate(from);
    const end = toDate(to);
    if (!(start && end)) {
      return "";
    }
    return formatter
      .formatRangeToParts(start, end)
      .map(({ type, value }) =>
        type === "day" && unpadDay ? String(Number(value)) : value
      )
      .join("");
  }

  return {
    /**
     * A country's name from an alpha-2 or alpha-3 code (MRZ nationalities are
     * alpha-3); "" for no code, the code itself when no country has it.
     */
    country(code: string | null | undefined): string {
      if (!code) {
        return "";
      }
      return countryName(alpha3ToAlpha2(code) ?? code, locale) ?? code;
    },
    /** The picker option a stored country code reads as, in this language. */
    countryOption(value: string, code: "alpha2" | "alpha3"): CountryOption {
      return countryOptionFor(value, code, locale);
    },
    /** Every country as a picker option, named and sorted in this language. */
    countryOptions(code: "alpha2" | "alpha3"): CountryOption[] {
      return countryOptions(code, locale);
    },
    date,
    dateRange,
    /**
     * A day as a list or a transcript heads it: "Today", "Yesterday" or
     * "Tomorrow"; the weekday within a week either way; else "Sun 22 Nov",
     * or "22 Nov 2025" in another year.
     */
    dayLabel(
      value: Date | string,
      timeZone: string | null | undefined,
      now: Date = new Date()
    ): string {
      const zone = zoneOrDevice(timeZone);
      const day = localDateInTimezone(value, zone);
      const today = localDateInTimezone(now, zone);
      const days = daysBetween(today, day);
      if (Math.abs(days) < DAYS_IN_WEEK) {
        return relativeDay(days) ?? date(value, "weekday", zone);
      }
      const sameYear =
        day.slice(0, YEAR_LENGTH) === today.slice(0, YEAR_LENGTH);
      return date(value, sameYear ? "day" : "mediumDate", zone);
    },
    /** "2.5 MB", "512 kB": a file's size as a host would say it. */
    fileSize(bytes: number): string {
      const { amount, fractionDigits, unit } = attachmentSizeReading(bytes);
      return format.number(amount, {
        maximumFractionDigits: fractionDigits,
        style: "unit",
        unit,
        useGrouping: false,
      });
    },
    /** "Casa W, Sava and Vila": items joined the way this language lists them. */
    list(items: readonly string[]): string {
      return format.list(items);
    },
    locale,
    /** "€1,155.00": an amount in minor units, in its currency. */
    money(amountMinor: number, currency: string | null): string {
      return formatMinor(amountMinor, currency, tag);
    },
    /**
     * "120", "312,66": a price without its currency, as on the board and in a
     * price field, which `parsePriceToMinor` reads back unchanged.
     */
    rateAmount(amountMinor: number, currency: string): string {
      const { amount, fractionDigits } = rateAmountReading(
        amountMinor,
        currency
      );
      return format.number(amount, {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
        useGrouping: false,
      });
    },
    /**
     * "Today", "Yesterday" or "Tomorrow" for `value` against `now`, by
     * calendar day in `timeZone`; null for any other day.
     */
    relativeDay(
      value: Date | string,
      timeZone: string | null | undefined,
      now: Date = new Date()
    ): string | null {
      const zone = zoneOrDevice(timeZone);
      return relativeDay(
        daysBetween(
          localDateInTimezone(now, zone),
          localDateInTimezone(value, zone)
        )
      );
    },
    /**
     * A run of stay days ('YYYY-MM-DD'): "Sun 22 Nov" for one, else the
     * range, "22–28 Nov 2026".
     */
    span(from: string, to: string): string {
      return from === to
        ? date(from, "day", STAY_DAY_ZONE)
        : dateRange(from, to, "mediumDate", STAY_DAY_ZONE);
    },
    /**
     * A wall clock already resolved against its zone ('HH:mm'), written the
     * way this language writes times; the input itself when it is not one.
     */
    wallClock(hhmm: string): string {
      const parsed = parseLocalTime(hhmm);
      if (!parsed) {
        return hhmm;
      }
      return date(
        new Date(Date.UTC(2000, 0, 1, parsed.hours, parsed.minutes)),
        "time",
        STAY_DAY_ZONE
      );
    },
    /** The short name of a weekday, by `Date.getDay()` index: "Sun". */
    weekdayShort(dayIndex: number): string {
      return format.dateTime(A_SUNDAY + dayIndex * MS_PER_DAY, {
        timeZone: STAY_DAY_ZONE,
        weekday: "short",
      });
    },
  };
}

export type InterfaceFormat = ReturnType<typeof formatFor>;

const formats = new Map<HostLocale, InterfaceFormat>();

function cachedFormatFor(locale: HostLocale): InterfaceFormat {
  let cached = formats.get(locale);
  if (!cached) {
    cached = formatFor(locale);
    formats.set(locale, cached);
  }
  return cached;
}

/**
 * The host app's one date, number and country formatter, in the interface
 * language, for a component. Every date names its zone, as
 * `InterfaceLocaleRoot` asks.
 */
export function useInterfaceFormat(): InterfaceFormat {
  return cachedFormatFor(useLocale());
}

/**
 * The same formatter outside React, in the interface language on screen when
 * it is called. Call it where the words are needed, never at module load.
 */
export function interfaceFormat(): InterfaceFormat {
  return cachedFormatFor(getInterfaceLocaleState().locale);
}
